<template>
  <main class="w-full">
    <section class="max-sm:px-6 max-xl:px-13 py-24 bg-grey-dark-900">
      <div class="max-w-content mx-auto">
        <div class="flex flex-col items-center text-center gap-6 max-w-3xl mx-auto mb-12">
          <p class="text-lg text-grey-dark-300 font-bold">Developers</p>
          <h1 class="text-4xl md:text-5xl font-semibold text-grey-dark-50 leading-tight">
            Seamless software integrations
          </h1>
          <p class="text-xl text-grey-dark-300 leading-relaxed">
            LayUp integrates seamlessly at any level, offering an unparalleled recurring payments solution for
            businesses.
          </p>
        </div>
        <div class="flex flex-col sm:flex-row items-center justify-center gap-4">
          <router-link :to="{ name: 'business-enquiry' }">
            <Button variant="outline-light" size="business" class="text-lg w-full md:w-auto cursor-pointer"
              >Talk to sales</Button
            >
          </router-link>
          <a :href="signUpUrl">
            <Button size="business" class="text-lg w-full md:w-auto cursor-pointer">Get started</Button>
          </a>
        </div>
      </div>
    </section>
    <section>
      <FeaturesSection
        :features="features"
        :hasButton="false"
        tag="Simple integration"
        title="Offer customers a brand new way to pay"
        description="Powering growth for 2,000+ merchants."
        :itemsPerRow="4"
      />
    </section>

    <section class="max-w-content mx-auto max-sm:px-6 max-xl:px-13 py-12">
        <IntegrationInfoTabs :tabs="tabs" />
    </section>

    <section>
      <FeaturesSection
        :features="plugins"
        :hasButton="true"
        tag="Plugins"
        title="Integrate with your software"
        description="Easily merge with one of our plugins."
        :itemsPerRow="4"
      />
    </section>
    <section class="py-24">
      <!-- Social Proof Section -->
      <AnimationComponent>
        <MerchantLogoCarousel variant="light" />
      </AnimationComponent>
    </section>

    <AnimationComponent>
      <ProductShowcaseSection :viewMoreButton="true" :showButton="false" />
    </AnimationComponent>

    <CtaSection :title="getStartedCta.title" :description="getStartedCta.description" :showTalkToSales="false" :add-padding-x="!isDesktop" />
  </main>
</template>

<script setup lang="ts">
import magentoLogo from '@/assets/images/magentoLogo.svg';
import shopifyLogo from '@/assets/images/shopifyLogo.svg';
import wooCommerceLogo from '@/assets/images/wooCommerceLogo.svg';
import AnimationComponent from '@/components/AnimationComponent.vue';
import CtaSection from '@/components/business/CtaSection.vue';
import FeaturesSection, { type Feature } from '@/components/business/FeaturesSection.vue';
import ProductShowcaseSection from '@/components/business/ProductShowcaseSection.vue';
import IntegrationInfoTabs from '@/components/IntegrationInfoTabs.vue';
import MerchantLogoCarousel from '@/components/MerchantLogoCarousel.vue';
import { Button } from '@/components/ui/button';
import { DASHBOARDS_URL } from '@/config';
import integrationInfo from '@/data/integrationInformation.json';
import { computed } from 'vue';
import { useMediaQuery } from '@vueuse/core';

const isDesktop = useMediaQuery('(min-width: 1024px)');

const features = [
  {
    icon: 'RefreshCcw',
    title: 'Easy to Integrate',
    description: 'All you need is an API key to get started.',
  },
  {
    icon: 'Rocket',
    title: 'User-friendly',
    description: 'Simple and easy to use for businesses of all sizes.',
  },
  {
    icon: 'TrendingUp',
    title: 'Market-ready',
    description: 'Fully licensed Third Party Payment Provider via PASA.',
  },
  {
    icon: 'TrendingUp',
    title: 'Constantly updated',
    description: 'Agile tech team, always innovating to improve.',
  },
];

const plugins = [
  {
    image: shopifyLogo,
    title: 'Shopify',
    description: '',
    buttonLabel: 'View',
    buttonStyle: 'link-light' as Feature['buttonStyle'],
    buttonLink: '/business/plugin/shopify',
  },
  {
    image: wooCommerceLogo,
    title: 'WooCommerce',
    description: '',
    buttonLabel: 'View',
    buttonStyle: 'link-light' as Feature['buttonStyle'],
    buttonLink: '/business/plugin/woocommerce',
  },
  {
    image: magentoLogo,
    title: 'Magento',
    description: '',
    buttonLabel: 'View',
    buttonStyle: 'link-light' as Feature['buttonStyle'],
    buttonLink: '/business/plugin/magento',
  },
  {
    icon: 'Settings',
    title: 'API Documentation',
    description: '',
    buttonLabel: 'View',
    buttonStyle: 'link-light' as Feature['buttonStyle'],
    buttonLink: 'https://documentation.layup.co.za/',
  },
];
const getStartedCta = {
  title: 'Get started with LayUp',
  description: 'Low transaction fees and flexible settlements, all without a monthly contract.',
};

const signUpUrl = computed((): string => {
  return `${DASHBOARDS_URL}/merchant/auth/sign-up`;
});
const tabs = computed(() => integrationInfo.find((item) => item.category === 'integrationInformation')?.tabs || []);
</script>
